import React, { useState } from 'react';
import { IOnFailure, IOnSuccess, MaybeCognitoError } from '@move-frontend/auth';
import { FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import { AuthFormSection as Section } from '../../../shared/form-layout/AuthFormSection';
import { AuthFormRow as Row } from '../../../shared/form-layout/AuthFormRow';
import { Password } from '../../../shared/form-fields/Password';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { ConfirmPassword } from '../../../shared/form-fields/ConfirmPassword';
import { useChangePasswordForm } from './hooks/useChangePasswordForm';
import { useChangePasswordSubmit } from './hooks/useChangePasswordSubmit';
import { ChangePasswordSubmit } from './ChangePasswordSubmit';

export interface IChangePasswordFormProps {
	success: IOnSuccess<void>;
	failure: IOnFailure;
}

export const ChangePasswordForm = observer(
	({ success, failure }: IChangePasswordFormProps) => {
		const { t } = useTranslation();

		const form = useChangePasswordForm();
		const [isSubmitting, setIsSubmitting] = useState(false);

		const onSuccess = () => {
			setIsSubmitting(false);
			success();
		};

		const onFailure = (e: MaybeCognitoError) => {
			setIsSubmitting(false);
			failure(e);
		};

		const { performChangePassword } = useChangePasswordSubmit({
			onSuccess,
			onFailure,
		});

		const onSubmit = () => {
			setIsSubmitting(true);
			performChangePassword(form.getValues());
		};

		return (
			<FormProvider {...form}>
				<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
					<Section>
						<Row columns={[2, { mobileXL: 1 }]}>
							<Password
								name="currentPassword"
								label={t('account_change_password_current_password')}
								autoComplete="current-password"
								disabled={isSubmitting}
							/>
						</Row>
					</Section>
					<Section>
						<Row columns={[2, { mobileXL: 1 }]}>
							<Password
								label={t('user_form_new_password_label')}
								validateConfirmation
								autoComplete="new-password"
								disabled={isSubmitting}
							/>
							<ConfirmPassword
								label={t('user_form_confirm_new_password_label')}
								disabled={isSubmitting}
							/>
						</Row>
					</Section>
					<ChangePasswordSubmit />
				</form>
			</FormProvider>
		);
	},
);
